import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {RIPPLE_RINGS_KIND} from '../../../configs/biomeEffects/rippleRings';
import {resolveSpawnBox} from './biomeContext';
import {lerp, pick, randomIn, smoothstep, writeHiddenPose, writePose} from './biomeMath';

/**
 * КРУГИ НА ВОДЕ (подложка с рисовыми террасами). Чистые функции без Phaser и
 * DOM: по этому описанию частицы каждый кадр ставит сцена
 * (scenes/FieldBiomeEffects).
 *
 * Всплеск — точка на воде, из которой одно за другим расходятся два-три
 * кольца. Кольцо растет быстро и замедляется, проступает в начале и тает к
 * концу. Кольцо — две частицы: темная ложбина и светлый гребень поверх нее;
 * ложбина чуть крупнее и сдвинута вправо-вниз, так что выглядывает из-под
 * гребня темной каймой.
 *
 * Всплески рождаются только на ячейках поля, лежащих на воде пруда, и там,
 * куда смотрит камера. Ветра у эффекта нет: круги на воде от него не зависят.
 */

const CREST = 'crest';
const TROUGH = 'trough';

// Гребень полосы тонкого кольца лежит на этой доле полукадра текстуры (см.
// utils/effects/fieldBiomeTextures): кадр шире самого кольца. Тест сверяет
// число с пикселями текстуры
export const RIPPLE_CREST_SHARE = 0.84;

// Шаг ячеек поля по горизонтали и вертикали, размеров ячейки: прямоугольник
// такого размера вокруг центра ячейки укладывает поле без пропусков
const CELL_STEP_X = 1.5;
const CELL_STEP_Y = Math.sqrt(3);

const createTracks = (context, params) => {
  const {hexSize, cells} = context;
  const {splash, ring} = params;
  const water = context.toWorldOval(params.pond);

  // Середина всплеска держится от кромки воды на размер выросшего кольца (с
  // ложбиной: она крупнее гребня и сдвинута), а ячейка годится для всплесков,
  // только если этот запас выдержан в любой ее точке. Размер кольца задан в
  // ячейках, вода — в долях картинки, поэтому запас считается здесь, под поле
  const ringReach = hexSize * (
    ring.endRadiusFactor.max * ring.trough.scale + Math.hypot(ring.trough.shiftFactor.x, ring.trough.shiftFactor.y)
  );
  const cellReach = (hexSize * Math.hypot(CELL_STEP_X, CELL_STEP_Y)) / 2;
  const reachX = water.radiusX - ringReach - cellReach;
  const reachY = water.radiusY - ringReach - cellReach;
  // Ячейки поля на воде пруда: края поля могут лежать на террасах, там кругов нет
  const pondCells = reachX > 0 && reachY > 0
    ? cells.filter(({x, y}) => Math.hypot((x - water.x) / reachX, (y - water.y) / reachY) <= 1)
    : [];

  const buildGroup = ({rng, view}) => {
    // Там, куда смотрит камера
    const box = resolveSpawnBox(context, view);
    const cellsInView = pondCells.filter(({x, y}) => (
      x >= box.left && x <= box.right && y >= box.top && y <= box.bottom
    ));
    if (cellsInView.length === 0) {
      return null;
    }
    // Темп — по числу ячеек пруда в кадре: пропущенная очередь возвращается
    // через короткую паузу контроллера, и всплесков выходит во столько же раз
    // меньше
    const rateShare = Math.max(params.minRateShare, Math.min(1, cellsInView.length / params.fullRateCells));
    if (rng() >= rateShare) {
      return null;
    }

    // Где угодно в пределах ячейки, но не у ее центра: круг на воде — не
    // метка на ячейке
    const cell = pick(rng, cellsInView);
    let offsetX = (rng() - 0.5) * hexSize * CELL_STEP_X;
    let offsetY = (rng() - 0.5) * hexSize * CELL_STEP_Y;
    const minOffset = hexSize * splash.minCellOffsetFactor;
    const offset = Math.hypot(offsetX, offsetY);
    if (offset < minOffset) {
      // Слишком близко к центру — отодвигаем в ту же сторону (из самого
      // центра — вправо)
      offsetX = offset > 0 ? (offsetX / offset) * minOffset : minOffset;
      offsetY = offset > 0 ? (offsetY / offset) * minOffset : 0;
    }

    const ringCount = splash.ringCount + (rng() < splash.extraRingChance ? 1 : 0);
    const lifeMs = randomIn(rng, ring.lifeMs);
    const particles = [];
    for (let index = 0; index < ringCount; index += 1) {
      const delayMs = index * splash.ringStaggerMs;
      // Ложбина — под гребнем
      particles.push({
        kind: TROUGH, layer: FIELD_BIOME_LAYER_KEYS.GROUND, sublayer: 0, texture: FIELD_BIOME_TEXTURES.RIPPLE,
        tint: ring.trough.tint, alpha: ring.trough.alpha, delayMs,
      });
      particles.push({
        kind: CREST, layer: FIELD_BIOME_LAYER_KEYS.GROUND, sublayer: 1, texture: FIELD_BIOME_TEXTURES.RIPPLE,
        tint: ring.crest.tint, alpha: ring.crest.alpha, delayMs,
      });
    }

    return {
      durationMs: (ringCount - 1) * splash.ringStaggerMs + lifeMs,
      x: cell.x + offsetX,
      y: cell.y + offsetY,
      lifeMs,
      startRadius: hexSize * ring.startRadiusFactor,
      endRadius: hexSize * randomIn(rng, ring.endRadiusFactor),
      particles,
    };
  };

  const resolvePose = (group, particle, elapsedMs, out) => {
    const ageMs = elapsedMs - particle.delayMs;
    if (!(ageMs > 0) || !(ageMs < group.lifeMs)) {
      return writeHiddenPose(out);
    }
    const life = ageMs / group.lifeMs;
    // Кольцо растет быстро и замедляется к концу
    const radius = lerp(group.startRadius, group.endRadius, 1 - (1 - life) ** 2);
    const alpha = particle.alpha * smoothstep(0, ring.fadeInShare, life) * (1 - life) ** ring.fadePower;
    // Кадр текстуры шире кольца: гребень полосы лежит на доле полукадра
    const size = (radius * 2) / RIPPLE_CREST_SHARE;
    if (particle.kind === TROUGH) {
      return writePose(
        out,
        group.x + hexSize * ring.trough.shiftFactor.x,
        group.y + hexSize * ring.trough.shiftFactor.y,
        alpha, size * ring.trough.scale, size * ring.trough.scale, 0
      );
    }
    return writePose(out, group.x, group.y, alpha, size, size, 0);
  };

  return [{
    schedule: {
      firstDelayMs: params.firstDelayMs,
      intervalMs: params.intervalMs,
      maxGroups: params.maxGroups,
      prewarm: params.prewarm,
    },
    buildGroup,
    resolvePose,
  }];
};

const rippleRings = {
  kind: RIPPLE_RINGS_KIND,
  // Ветер матча эффекту не нужен: при смене ветра его дорожки не трогаются
  usesWind: false,
  textures: [FIELD_BIOME_TEXTURES.RIPPLE],
  createTracks,
};

export default rippleRings;
